import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../Assets/logooo-removebg-preview.png';
import { ThemeContext } from '../utils/context';
import { useAuth } from '../utils/AuthProvider';
import { senvraApi } from '../utils/axiosInstance'; 
// Make sure this path is correct

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, setIsDarkMode, setLoggedIn } = useContext(ThemeContext);
  const { user, logout, auth, setAuth } = useAuth();

  // UI States
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);

  // Edit Name Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [nameError, setNameError] = useState("");
  const [nameSuccess, setNameSuccess] = useState("");

  // Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check existing auth from localStorage
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token && !auth) {
      setAuth(true);
      setLoggedIn(true);
    } else if (!token && auth) {
      setAuth(false);
      setLoggedIn(false);
    }
  }, [auth, setAuth, setLoggedIn]);

  const isActive = (path) => location.pathname === path;

  // Mobile Accordion Toggle
  const toggleMobileSection = (section) => {
    setMobileOpenSection(mobileOpenSection === section ? null : section);
  };

  // Logout Handler
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    logout();
    
    setAuth(false);
    setLoggedIn(false);
    setUserDropdown(false);
    setIsMobileMenuOpen(false);
    
    navigate('/');
  };

  // Get user data from localStorage directly for reliability
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
    return user || null;
  };

  const currentUser = getUserData();

  // --- New Edit Name Handlers ---
  const openEditModal = () => {
    setEditNameValue(currentUser?.name || "");
    setNameError("");
    setNameSuccess("");
    setIsEditModalOpen(true);
    setUserDropdown(false);
    setIsMobileMenuOpen(false);
  };

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    if (!editNameValue.trim()) return;

    setIsUpdatingName(true);
    setNameError("");
    setNameSuccess("");

    try {
      const response = await senvraApi.put('/profile/updateName', { name: editNameValue });
      
      // Update local storage so the Navbar reflects the change immediately
      const currentData = JSON.parse(localStorage.getItem('user') || '{}');
      currentData.name = editNameValue;
      localStorage.setItem('user', JSON.stringify(currentData));

      setNameSuccess(response.data?.message || "Name updated successfully");
      
      // Auto close modal after showing success
      setTimeout(() => {
        setIsEditModalOpen(false);
        setNameSuccess("");
      }, 1500);

    } catch (error) {
      setNameError(error.response?.data?.message || "Failed to update name");
    } finally {
      setIsUpdatingName(false);
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-500 ${
        !isScrolled ? 'md:mt-4 mt-0' : 'mt-0'
      }`}>
        <div className={`w-full transition-all duration-500 flex justify-between items-center px-6 md:px-12 h-[70px] ${
          !isScrolled ? 'bg-transparent' : 'bg-white dark:bg-slate-900 shadow-sm'
        }`}>
          {/* Logo */}
          <div className="logo flex items-center h-full min-w-[150px]">
            <Link to="/" className="flex items-center h-full">
              <img 
                src={logo} 
                alt="SENVRA AI" 
                className="w-[150px] h-[80px] object-contain" 
              />
            </Link>
          </div>

          {/* Desktop Navigation - THE PILL CONTAINER */}
          <nav className="hidden md:flex items-center gap-1 bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 px-4 py-1.5 rounded-full shadow-sm transition-all duration-300">
            <Link to="/" className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all ${
              isActive('/') 
                ? 'text-blue-600' 
                : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
            }`}>
              Home
            </Link>

            {/* Internships Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('internships')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all flex items-center gap-1 ${
                isActive('/apply') || isActive('/verify')
                  ? 'text-blue-600'
                  : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
              }`}>
                Internships
                <svg className={`w-3 h-3 transition-transform ${activeDropdown === 'internships' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`absolute top-full left-0 pt-2 w-48 transition-all duration-200 ${
                activeDropdown === 'internships' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2">
                  <Link to="/apply" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Apply Now
                  </Link>
                  <Link to="/verify" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Verify Certificate
                  </Link>
                </div>
              </div>
            </div>

            {/* Skill Course Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('skillcourse')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all flex items-center gap-1 ${
                isActive('/dashboards') || isActive('/browse-courses') || isActive('/my-certificates') || isActive('/skill-certificate-verification')
                  ? 'text-blue-600'
                  : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
              }`}>
                Skill Course
                <svg className={`w-3 h-3 transition-transform ${activeDropdown === 'skillcourse' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`absolute top-full left-0 pt-2 w-64 transition-all duration-200 ${
                activeDropdown === 'skillcourse' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2">
                  <Link to="/dashboards" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Dashboards
                  </Link>
                  <Link to="/browse-courses" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Browse Courses
                  </Link>
                  <Link to="/my-certificates" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    My Certificates
                  </Link>
                  {/* <Link to="/skill-certificate-verification" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Skill Certificate Verification
                  </Link> */}
                </div>
              </div>
            </div>

            {/* Career Tools Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('career')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all flex items-center gap-1 ${
                isActive('/ats-score') || isActive('/email-builder') || isActive('/interview-prep')
                  ? 'text-blue-600'
                  : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
              }`}>
                Career Tools
                <svg className={`w-3 h-3 transition-transform ${activeDropdown === 'career' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`absolute top-full left-0 pt-2 w-52 transition-all duration-200 ${
                activeDropdown === 'career' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2">
                  <Link to="/ats-score" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Check ATS Score
                  </Link>
                  <Link to="/email-builder" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Job Email Builder
                  </Link>
                  <Link to="/interview-prep" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Interview Preparation
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/services" className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all ${
              isActive('/services')
                ? 'text-blue-600'
                : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
            }`}>
              Services
            </Link>

            {/* More Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('more')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className={`text-[13px] font-semibold px-4 py-2 rounded-full transition-all flex items-center gap-1 ${
                isActive('/reviews') || isActive('/contact') || isActive('/terms-and-conditions') || isActive('/privacy-policy') || isActive('/cookies-policy')
                  ? 'text-blue-600'
                  : 'text-slate-600 dark:text-slate-300 hover:text-blue-600'
              }`}>
                More
                <svg className={`w-3 h-3 transition-transform ${activeDropdown === 'more' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`absolute top-full right-0 pt-2 w-56 transition-all duration-200 ${
                activeDropdown === 'more' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2">
                  <Link to="/reviews" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Student Reviews
                  </Link>
                  <Link to="/contact" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Contact Support
                  </Link>
                  <Link to="/terms-and-conditions" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Terms & Conditions
                  </Link>
                  <Link to="/privacy-policy" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Privacy Policy
                  </Link>
                  <Link to="/cookies-policy" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Cookies Policy
                  </Link>
                  <Link to="/refer-earn" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                    Refer & Earn
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* Right Side Buttons */}
          <div className="flex items-center gap-3">
            {/* <button 
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button> */}

            {/* If auth = true -> Show User Profile & Logout */}
            {auth ? (
              <div className="relative" onMouseEnter={() => setUserDropdown(true)} onMouseLeave={() => setUserDropdown(false)}>
                <button className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 px-3 rounded-full hover:shadow-md transition">
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs uppercase">
                    {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200 hidden sm:block">
                    {currentUser?.name || "User"} ▾
                  </span>
                </button>
                
                <div className={`absolute top-[80%] right-0 pt-4 w-56 transition-all duration-200 ${userDropdown ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}`}>
                  <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 p-4 z-[1001]">
                    
                    <div className="mb-3">
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Signed in as</p>
                      <p className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">
                        {currentUser?.email || currentUser?.name || 'User'}
                      </p>
                    </div>

                    {/* NEW EDIT NAME BUTTON */}
                    <button 
                      onClick={openEditModal}
                      className="w-full flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 py-2 px-3 rounded-xl transition font-bold text-sm mb-1"
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Edit Name
                    </button>

                    <Link 
                      to="/change-password" 
                      onClick={() => setUserDropdown(false)}
                      className="w-full flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 py-2 px-3 rounded-xl transition font-bold text-sm mb-1"
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      Change Password
                    </Link>

                    <button onClick={handleLogout} className="w-full flex items-center gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 py-2 px-3 rounded-xl transition font-bold text-sm">
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
                      </svg>
                      Logout
                    </button>

                  </div>
                </div>
              </div>
            ) : (
              // If auth = false -> Show Login & Register buttons
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/login" className="py-2.5 px-5 rounded-2xl font-bold text-[13px] text-black dark:text-slate-200 transition hover:text-blue-500 hover:opacity-90 shadow-md">
                  Login
                </Link>
                <Link to="/register" className="py-2.5 px-5 rounded-2xl font-bold text-[13px] bg-[#0f172a] dark:bg-white text-white dark:text-slate-900 transition hover:opacity-90 hover:shadow-md shadow-lg">
                  Join Now →
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center text-2xl"
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div className={`fixed top-[70px] left-0 w-full h-[calc(100vh-70px)] bg-white dark:bg-slate-900 z-[999] p-5 transition-transform duration-300 border-t border-slate-100 dark:border-slate-800 overflow-y-auto ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col gap-1">
          {/* Show User Info if logged in */}
          {auth && currentUser && (
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-3xl mb-6 border border-slate-100 dark:border-slate-700/50">
              <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xl uppercase shrink-0">
                {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-black text-slate-900 dark:text-white truncate">{currentUser.name || "User"}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{currentUser.email || ""}</p>
              </div>
            </div>
          )}
          
          <Link to="/" className="text-base font-semibold py-3 border-b border-slate-100 dark:border-slate-800" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          
          <div className="border-b border-slate-100 dark:border-slate-800">
            <button onClick={() => toggleMobileSection('internships')} className="w-full text-base font-semibold py-3 flex justify-between items-center">
              Internships <span>{mobileOpenSection === 'internships' ? '▴' : '▾'}</span>
            </button>
            <div className={`pl-4 overflow-hidden transition-all duration-300 ${mobileOpenSection === 'internships' ? 'max-h-40 pb-3' : 'max-h-0'}`}>
              <Link to="/apply" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Apply Now</Link>
              <Link to="/verify" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Verify Certificate</Link>
            </div>
          </div>

          <div className="border-b border-slate-100 dark:border-slate-800">
            <button onClick={() => toggleMobileSection('skillcourse')} className="w-full text-base font-semibold py-3 flex justify-between items-center">
              Skill Course <span>{mobileOpenSection === 'skillcourse' ? '▴' : '▾'}</span>
            </button>
            <div className={`pl-4 overflow-hidden transition-all duration-300 ${mobileOpenSection === 'skillcourse' ? 'max-h-60 pb-3' : 'max-h-0'}`}>
              <Link to="/dashboards" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Dashboards</Link>
              <Link to="/browse-courses" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Browse Courses</Link>
              <Link to="/my-certificates" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>My Certificates</Link>
              {/* <Link to="/skill-certificate-verification" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Skill Certificate Verification</Link> */}
            </div>
          </div>

          <div className="border-b border-slate-100 dark:border-slate-800">
            <button onClick={() => toggleMobileSection('career')} className="w-full text-base font-semibold py-3 flex justify-between items-center">
              Career Tools <span>{mobileOpenSection === 'career' ? '▴' : '▾'}</span>
            </button>
            <div className={`pl-4 overflow-hidden transition-all duration-300 ${mobileOpenSection === 'career' ? 'max-h-60 pb-3' : 'max-h-0'}`}>
              <Link to="/ats-score" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Check ATS Score</Link>
              <Link to="/email-builder" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Job Email Builder</Link>
              <Link to="/interview-prep" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Interview Preparation</Link>
            </div>
          </div>

          <Link to="/services" className="text-base font-semibold py-3 border-b border-slate-100 dark:border-slate-800" onClick={() => setIsMobileMenuOpen(false)}>Services</Link>

          <div className="border-b border-slate-100 dark:border-slate-800">
            <button onClick={() => toggleMobileSection('more')} className="w-full text-base font-semibold py-3 flex justify-between items-center">
              More <span>{mobileOpenSection === 'more' ? '▴' : '▾'}</span>
            </button>
            <div className={`pl-4 overflow-hidden transition-all duration-300 ${mobileOpenSection === 'more' ? 'max-h-60 pb-3' : 'max-h-0'}`}>
              <Link to="/reviews" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Student Reviews</Link>
              <Link to="/contact" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Contact Support</Link>
              <Link to="/terms-and-conditions" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Terms & Conditions</Link>
              <Link to="/privacy-policy" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Privacy Policy</Link>
              <Link to="/cookies-policy" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}>Cookies Policy</Link>
              <Link to="/refer-earn" className="block py-2 text-sm text-slate-500" onClick={() => setIsMobileMenuOpen(false)}> Refer & Earn</Link>
            </div>
          </div>

          {/* Mobile Menu Buttons based on auth */}
          {auth ? (
            <div className="mt-8 flex flex-col gap-3">
              <button 
                onClick={openEditModal}
                className="w-full py-4 rounded-xl font-bold text-base bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-100 dark:border-slate-700 flex items-center justify-center gap-2"
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit Name
              </button>

              <button onClick={handleLogout} className="w-full py-4 rounded-xl font-bold text-base bg-red-50 dark:bg-red-900/10 text-red-500 border border-red-100 dark:border-red-900/30 flex items-center justify-center gap-2">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
                Logout Account
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 mt-8">
              <Link to="/login" className="py-2.5 px-5 rounded-2xl font-bold text-[13px] text-black dark:text-slate-200 transition hover:text-blue-500 hover:opacity-90 shadow-md text-center" onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
              <Link to="/register" className="py-2.5 px-5 rounded-2xl font-bold text-[13px] bg-[#0f172a] dark:bg-white text-white dark:text-slate-900 transition hover:opacity-90 hover:shadow-md shadow-lg text-center" onClick={() => setIsMobileMenuOpen(false)}>
                Join Now →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* --- EDIT NAME MODAL --- */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[24px] shadow-2xl border border-slate-100 dark:border-slate-800 p-6 relative">
            <button 
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Edit Profile Name</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Update how your name appears across the platform.</p>
            </div>
            
            {nameSuccess && (
              <div className="mb-4 p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm font-bold border border-emerald-100">
                ✅ {nameSuccess}
              </div>
            )}
            {nameError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm font-bold border border-red-100 animate-shake">
                ⚠️ {nameError}
              </div>
            )}

            <form onSubmit={handleNameUpdate}>
              <div className="mb-6">
                <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">
                  Full Name
                </label>
                <div className="relative">
                  <svg className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <input 
                    type="text" 
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-50 dark:bg-slate-800 border-none text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder="Enter your name"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isUpdatingName || !editNameValue.trim()}
                  className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-[#0091ea] hover:bg-[#0081d5] disabled:opacity-70 transition-all flex justify-center items-center gap-2"
                >
                  {isUpdatingName ? (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  ) : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div> 
      )}
    </>
  );
};

export default Navbar;